import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";
import { useAtom } from "jotai";
import { pharmacyAtom } from "../../_helper/constant/constant-atom";

const DEFAULT_FORM_DATA = {
  description: "",
  quantity: 1,
  unitPrice: 0,
  cgstRate: 6,
  sgstRate: 6,
  igstRate: 6,
  code: "",
  lineDiscountType: "lumpSum",
  lineDiscountValue: 0,
  batchNo: "",
  availableQuantity: 0, // This will be updated dynamically from batchData
};

const ItemModal = ({ item, onClose, onSave, pharmacy_id }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [searchInput, setSearchInput] = useState(null);

  // console.log("Current Item", item);

  // Fetch items for the item select
  const { data: itemData, isLoading } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getItem&items_name=${searchInput}&pharmacy_id=${pharmacy_id}`,
    !!searchInput
  );

  // Fetch batch options based on the current item code
  const { data: batchData, isLoading: isBatchLoading } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getBatch&item_id=${formData.code}`
  );

  const { data: checkScheduleMedicine } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getScheduleTagging&drug_name=${formData?.itemName}`,
    batchData?.length > 0 ? true : false
  );

  console.log(checkScheduleMedicine);

  // When an item is passed via props, prefill the form and set selected item and batch.
  useEffect(() => {
    if (item?.itemName) {
      // Pre-fill formData with values from item
      setFormData({
        ...item,
        // Note: We do not set availableQuantity from item because we want to use batchData instead.
        batchNo: item.batchNo, // Use the batch number from the item for matching
      });

      // Pre-select item in the item select field.
      setSelectedItem({
        label: item.itemName,
        value: item.itemName,
      });
    }
  }, [item]);

  // Prepare item options for react-select
  const itemOptions =
    itemData?.map((itm) => ({
      value: itm.items_name || itm.name,
      label: itm.items_name || itm.name,
      data: itm,
    })) ?? [];

  // Prepare batch options for react-select (using batch_no as the value)
  const batchOptions =
    batchData?.map((batch) => ({
      value: batch.batch_no,
      label: `Batch: ${batch.batch_no} (Expiry: ${new Date(
        batch.expiry_date
      ).toLocaleDateString()}, Available: ${batch.quantity})`,
      data: batch,
    })) ?? [];

  // Auto-select matching batch option from batchOptions if formData.batchNo is set.
  useEffect(() => {
    if (formData.batchNo && batchOptions.length > 0) {
      const matchingBatch = batchOptions.find(
        (option) => option.value === formData.batchNo
      );
      if (matchingBatch) {
        setSelectedBatch(matchingBatch);
        // Update availableQuantity (and other fields if desired) from the batch data.
        setFormData((prev) => ({
          ...prev,
          availableQuantity: matchingBatch.data.quantity,
          // Optionally update these if you need them from batch data:
          expiryDate: matchingBatch.data.expiry_date,
          unitPrice: matchingBatch.data.price || prev.unitPrice,
          batchPrice: matchingBatch.data.batch_price || prev.batchPrice,
          sellingPrice: prev.sellingPrice || matchingBatch.data.price,
        }));
      }
    }
  }, [formData.batchNo]);

  // Calculate taxes based on current form data.
  const taxes = useMemo(() => {
    const quantity = Number(formData.quantity) || 0;
    const unitPrice = Number(formData.sellingPrice) || 0;
    const totalPrice = quantity * unitPrice;

    const cgst = (totalPrice * Number(formData.cgstRate || 0)) / 100;
    const sgst = (totalPrice * Number(formData.sgstRate || 0)) / 100;
    const igst = (totalPrice * Number(formData.igstRate || 0)) / 100;

    return {
      cgst,
      sgst,
      igst,
      totalTax: cgst + sgst + igst,
    };
  }, [
    formData.quantity,
    formData.unitPrice,
    formData.cgstRate,
    formData.sgstRate,
    formData.igstRate,
  ]);

  // Calculate discounted price
  const discountedPrice = useMemo(() => {
    const quantity = Number(formData.quantity) || 0;
    const sellingPrice = Number(formData.sellingPrice) || 0;
    const total = quantity * sellingPrice;

    const discount =
      formData.lineDiscountType === "percentage"
        ? (total * formData.lineDiscountValue) / 100
        : formData.lineDiscountValue;

    return Math.max(total - discount, 0);
  }, [
    formData.quantity,
    formData.unitPrice,
    formData.lineDiscountType,
    formData.lineDiscountValue,
    formData.sellingPrice,
  ]);

  const profitMargins = useMemo(() => {
    const batchPrice = Number(formData.batchPrice) || 0;
    const sellingPrice = Number(formData.sellingPrice) || 0;
    const mrp = Number(formData.unitPrice) || 0;

    const profitMarginMRP =
      mrp > 0 ? ((mrp - batchPrice) / batchPrice) * 100 : 0;
    const profitMarginSellingPrice =
      sellingPrice > 0 ? ((sellingPrice - batchPrice) / batchPrice) * 100 : 0;

    return {
      profitMarginMRP,
      profitMarginSellingPrice,
    };
  }, [formData.batchPrice, formData.sellingPrice, formData.unitPrice]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate quantity if it exceeds available quantity
    if (name === "quantity") {
      const enteredQuantity = Number(value);
      if (enteredQuantity > formData.availableQuantity) {
        toast.warn(
          `Quantity cannot exceed available quantity (${formData.availableQuantity}).`
        );
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // When an item is selected, update form data accordingly.
  const handleItemSelect = (selectedOption) => {
    setSelectedItem(selectedOption);
    if (selectedOption?.data) {
      setFormData((prev) => ({
        ...prev,
        itemName: selectedOption.label,
        description: selectedOption.data.description || "",
        unitPrice: selectedOption.data.price || 0,
        cgstRate: selectedOption.data.cgstRate || 0,
        sgstRate: selectedOption.data.sgstRate || 0,
        igstRate: selectedOption.data.igstRate || 0,
        code: selectedOption.data.id || "",
        // Reset batch related fields when a new item is selected
        batchNo: "",
        availableQuantity: 0,
      }));
      // Clear previously selected batch if any
      setSelectedBatch(null);
    }
  };

  // When a batch is selected, update form data accordingly.
  const handleBatchSelect = (selectedOption) => {
    setSelectedBatch(selectedOption);
    if (selectedOption?.data) {
      const locations = selectedOption?.data?.location?.map((location) => ({
        label: location.name,
        value: location.box_id,
      }));
      console.log(locations);
      setLocationOptions(locations);
      setFormData((prev) => ({
        ...prev,
        batchId: selectedOption.data.id,
        batchNo: selectedOption.value,
        expiryDate: selectedOption.data.expiry_date,
        availableQuantity: selectedOption.data.quantity, // Set available quantity dynamically
        unitPrice: selectedOption.data.price || 0, // Prefill price from batch
        batchPrice: selectedOption.data.batch_price || 0,
        sellingPrice: selectedOption.data.price || 0,
      }));
    } else {
      // Reset locationOptions when batch is deselected
      setLocationOptions([]);
      setFormData((prev) => ({
        ...prev,
        batchId: "",
        batchNo: "",
        expiryDate: "",
        availableQuantity: "",
        unitPrice: 0,
        batchPrice: 0,
        sellingPrice: 0,
      }));
    }
  };

  // Validate form before saving
  const validateForm = () => {
    const requiredFields = ["itemName", "quantity", "unitPrice", "batchNo"];
    const missingField = requiredFields.find((field) => !formData[field]);

    if (missingField) {
      const fieldName = missingField.replace(/([A-Z])/g, " $1").toLowerCase();
      toast.error(`Please fill in the ${fieldName} field.`);
      return false;
    }

    if (formData?.sellingPrice > formData?.unitPrice) {
      toast.warn(`Selling Price cannot be more than ${formData.unitPrice}`);
      return false;
    }

    if (formData.quantity > formData.availableQuantity) {
      toast.warn(
        `Quantity cannot exceed available quantity (${formData.availableQuantity}).`
      );
      return false;
    }

    return true;
  };

  // Handle save and close the modal
  const handleSave = () => {
    if (!validateForm()) return;
    onSave({
      ...formData,
      quantity: Number(formData?.quantity),
      unitPrice: Number(formData?.unitPrice),
      sellingPrice: Number(formData?.sellingPrice),
      cgstRate: Number(formData?.cgstRate),
      sgstRate: Number(formData?.sgstRate),
      igstRate: Number(formData?.igstRate),
      lineDiscountValue: Number(formData?.lineDiscountValue),
      schedule: checkScheduleMedicine[0]?.schedule,
      is_prescription_required:
        checkScheduleMedicine[0]?.is_prescription_required,
    });

    onClose();
  };

  // Helper to render a form field
  const renderFormField = (
    label,
    name,
    type = "text",
    required = false,
    isReadOnly = false,
    options = null
  ) => (
    <div>
      <label className="block text-gray-700 font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {options ? (
        <select
          name={name}
          value={formData[name]}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-400 focus:outline-none"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          readOnly={isReadOnly}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-400 focus:outline-none"
          min={type === "number" ? "0" : undefined}
          max={name === "quantity" ? formData.availableQuantity : undefined}
          required={required}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-lg overflow-auto h-[95%]">
        {/* Header with Close Button */}
        <div className="flex justify-between items-center bg-white top-0 z-10">
          <h3 className="text-2xl font-semibold text-gray-800">
            {item ? "Edit Item" : "Add Item"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✖
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Item Name <span className="text-red-500">*</span>
            </label>
            <Select
              options={itemOptions}
              value={selectedItem}
              onChange={handleItemSelect}
              isLoading={isLoading}
              isClearable
              isSearchable
              onInputChange={(value) => setSearchInput(value)}
              placeholder="Search and select item"
              className="w-full"
              classNamePrefix="react-select"
            />
          </div>

          {selectedItem && (
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Batch <span className="text-red-500">*</span>
              </label>
              <Select
                options={batchOptions}
                value={selectedBatch}
                onChange={handleBatchSelect}
                isLoading={isBatchLoading}
                isClearable
                isSearchable
                placeholder="Select batch"
                className="w-full"
                classNamePrefix="react-select"
              />
              {selectedBatch && (
                <p className="text-sm text-gray-600 mt-1">
                  Available Quantity: {formData.availableQuantity}
                </p>
              )}
            </div>
          )}

          {locationOptions.length > 0 && (
            <div className=" ">
              <label className="block text-lg font-semibold text-gray-800 mb-2">
                Medicine Storage Locations
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Below are the locations where the medicine is stored. You can
                check and pick it up accordingly.
              </p>
              <ul className="bg-white rounded-md p-1">
                {locationOptions.map((location, index) => (
                  <li
                    key={index}
                    className="p-2 border-b last:border-none hover:bg-gray-200 rounded-sm cursor-pointer"
                  >
                    {location.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <textarea
            name="description"
            placeholder="Enter description"
            value={formData.description}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-400 focus:outline-none"
            rows={1}
          />

          <div className="grid grid-cols-2 gap-4">
            {renderFormField("Quantity", "quantity", "number", true)}
            {renderFormField("Selling Price", "sellingPrice", "number", true)}
            {renderFormField("Batch Price", "batchPrice", "number", true, true)}
            {renderFormField("MRP", "unitPrice", "number", true, true)}
          </div>

          <div className="grid grid-cols-3 gap-4">
            {renderFormField("CGST Rate (%)", "cgstRate", "number")}
            {renderFormField("SGST Rate (%)", "sgstRate", "number")}
            {renderFormField("IGST Rate (%)", "igstRate", "number")}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {renderFormField(
              "Discount Type",
              "lineDiscountType",
              "select",
              false,
              false,
              [
                { value: "lumpSum", label: "Lump Sum" },
                { value: "percentage", label: "Percentage" },
              ]
            )}
            {renderFormField("Discount Value", "lineDiscountValue", "number")}
          </div>

          <div className="flex justify-between items-center mt-1">
            <p className="text-gray-700 font-medium">
              Profit Margin on Selling Price:
            </p>
            <p
              className={`text-lg font-bold ${
                profitMargins.profitMarginMRP >
                profitMargins.profitMarginSellingPrice
                  ? "text-red-600"
                  : "text-blue-600"
              }`}
            >
              {profitMargins?.profitMarginSellingPrice?.toFixed(2)}%
            </p>
          </div>

          <div className="flex justify-between items-center mt-2">
            <p className="text-gray-700 font-medium">Profit Margin on MRP:</p>
            <p className="text-lg font-bold text-green-600">
              {profitMargins?.profitMarginMRP?.toFixed(2)}%
            </p>
          </div>

          <div className="flex justify-between items-center mt-1">
            <p className="text-gray-700 font-medium">Total Tax:</p>
            <p className="text-lg font-bold text-blue-600">
              ₹{taxes.totalTax.toFixed(2)}
            </p>
          </div>

          <div className="flex justify-between items-center mt-1">
            <p className="text-gray-700 font-medium">
              Discounted Total Amount:
            </p>
            <p
              className={`text-lg font-bold ${
                discountedPrice <= 0 ? "text-red-600" : "text-green-600"
              }`}
            >
              ₹{discountedPrice.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemModal;
